import React, { useState } from 'react';
import { Collapse, Table, Select, InputNumber, Input, Button, Space, Popconfirm, Tag, message, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import api from '../api';

// One section of a help desk ticket's assets: 'impacted' (Impacted Assets) or
// 'shipment' (Shipments). Rows come from the page, which reloads them via
// onChanged after an add, edit or remove. Collapsed by default; the header
// carries the row count when there are any.
// readOnly: no add row, edit or remove buttons (not a member of the ticket's queue).
// Shipments also carry a required Direction.
const DIRECTION_OPTIONS = ['Send to Employee', 'Sent to Employee', 'Pending Return by Employee', 'Returned by Employee'].map((v) => ({ value: v, label: v }));

// extra: shown at the right of the header (the Shipments panel's link to the
// employee's shipping history).
export default function TicketAssetsCard({ ticketId, section, title, rows, assetTypes, onChanged, readOnly = false, extra = null }) {
  const { token } = theme.useToken();
  const [typeId, setTypeId] = useState(undefined);
  const [qty,    setQty]    = useState(1);
  const [desc,   setDesc]   = useState('');
  const [direction, setDirection] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const isShipment = section === 'shipment';
  // In-row edit: { id, typeId, qty, desc, direction }.
  const [edit,       setEdit]       = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const add = async () => {
    if (!typeId) { message.warning('Pick an asset.'); return; }
    if (isShipment && !direction) { message.warning('Pick a direction.'); return; }
    setSaving(true);
    try {
      await api.post(`/api/helpdesk/${ticketId}/assets`, {
        section, asset_type_id: typeId, qty: qty || 1, short_description: desc.trim() || null,
        ...(isShipment ? { direction } : {}),
      });
      setTypeId(undefined); setQty(1); setDesc(''); setDirection(undefined);
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add.');
    } finally {
      setSaving(false);
    }
  };

  // Shipments > Add Standard Equipment: every active type marked Standard
  // Equipment that the shipments do not have yet, Qty 1, with the Direction
  // picked in the add row.
  const standardMissing = isShipment ? assetTypes.filter((t) => t.IsStandard && !rows.some((r) => r.AssetTypeID === t.ID)) : [];
  const [stdSaving, setStdSaving] = useState(false);
  const addStandard = async () => {
    if (!direction) { message.warning('Pick a direction.'); return; }
    setStdSaving(true);
    try {
      await api.post(`/api/helpdesk/${ticketId}/shipments/standard`, { direction });
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add.');
    } finally {
      setStdSaving(false);
    }
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/api/helpdesk/${ticketId}/assets/${edit.id}`, {
        asset_type_id: edit.typeId, qty: edit.qty || 1, short_description: edit.desc.trim() || null,
        ...(isShipment ? { direction: edit.direction } : {}),
      });
      setEdit(null);
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/helpdesk/${ticketId}/assets/${id}`);
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to remove.');
    }
  };

  // Active types, plus the row's own type while editing it if that type has
  // since been deactivated, so the select can still show it.
  const typeOptions = (r) => {
    const opts = assetTypes.map((t) => ({ value: t.ID, label: t.Name }));
    if (r && !opts.some((o) => o.value === r.AssetTypeID)) {
      opts.unshift({ value: r.AssetTypeID, label: `${r.AssetTypeName} (inactive)` });
    }
    return opts;
  };

  const isEditing = (r) => edit?.id === r.ID;

  const columns = [
    { title: 'Asset', dataIndex: 'AssetTypeName', key: 'AssetTypeName',
      render: (v, r) => (isEditing(r)
        ? <Select size="small" showSearch optionFilterProp="label" style={{ width: '100%', minWidth: 160 }}
                  value={edit.typeId} onChange={(val) => setEdit({ ...edit, typeId: val })} options={typeOptions(r)} />
        : <>{v}{!r.AssetTypeIsActive && <Tag style={{ marginLeft: 6 }}>inactive</Tag>}</>) },
    { title: 'Qty', dataIndex: 'Qty', key: 'Qty', width: 90, align: 'right',
      render: (v, r) => (isEditing(r)
        ? <InputNumber size="small" min={1} precision={0} value={edit.qty} style={{ width: 70 }}
                       onChange={(val) => setEdit({ ...edit, qty: val || 1 })} />
        : v) },
    { title: 'Short Description', dataIndex: 'ShortDescription', key: 'ShortDescription',
      render: (v, r) => (isEditing(r)
        ? <Input size="small" maxLength={200} value={edit.desc} onChange={(e) => setEdit({ ...edit, desc: e.target.value })} />
        : v || '—') },
    ...(isShipment ? [{ title: 'Direction', dataIndex: 'Direction', key: 'Direction', width: 190,
      render: (v, r) => (isEditing(r)
        ? <Select size="small" style={{ width: '100%' }} placeholder="Direction" options={DIRECTION_OPTIONS}
                  value={edit.direction} onChange={(val) => setEdit({ ...edit, direction: val })} />
        : v || '—') }] : []),
    { title: 'Added By', dataIndex: 'CreatedByName', key: 'CreatedByName', width: 150, render: (v) => v || '—' },
    ...(readOnly ? [] : [{ title: '', key: 'actions', width: 90, align: 'right',
      render: (_, r) => (isEditing(r) ? (
        <Space size={4}>
          <Button size="small" type="primary" icon={<CheckOutlined />} loading={editSaving} onClick={saveEdit} />
          <Button size="small" icon={<CloseOutlined />} onClick={() => setEdit(null)} />
        </Space>
      ) : (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />}
                  onClick={() => setEdit({ id: r.ID, typeId: r.AssetTypeID, qty: r.Qty, desc: r.ShortDescription || '',
                                           direction: r.Direction || undefined })} />
          <Popconfirm title="Remove this row?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(r.ID)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )) }]),
  ];

  const body = (
    <>
      {/* Fields on one line, the buttons on the next. */}
      {!readOnly && <Space wrap style={{ marginBottom: 8 }}>
        <Select
          placeholder="Asset"
          showSearch
          optionFilterProp="label"
          style={{ width: 220 }}
          value={typeId}
          onChange={setTypeId}
          options={typeOptions(null)}
        />
        <InputNumber min={1} precision={0} value={qty} onChange={(v) => setQty(v || 1)} addonBefore="Qty" style={{ width: 120 }} />
        {isShipment && (
          // Wide enough for "Pending Return by Employee".
          <Select placeholder="Direction" style={{ width: 250 }} options={DIRECTION_OPTIONS}
                  value={direction} onChange={setDirection} />
        )}
        <Input placeholder="Short Description (optional)" maxLength={200} value={desc}
               onChange={(e) => setDesc(e.target.value)} style={{ width: 300 }} />
      </Space>}
      {!readOnly && <Space wrap style={{ display: 'flex', marginBottom: rows.length ? 12 : 0 }}>
        <Button icon={<PlusOutlined />} onClick={add} loading={saving}>Add</Button>
        {standardMissing.length > 0 && (
          <Button icon={<AppstoreAddOutlined />} onClick={addStandard} loading={stdSaving}
                  title={standardMissing.map((t) => t.Name).join(', ')}>
            Add All Standard Equipment
          </Button>
        )}
      </Space>}
      {readOnly && !rows.length && <span style={{ color: '#999' }}>None</span>}
      {rows.length > 0 && (
        <Table rowKey="ID" size="small" dataSource={rows} columns={columns} pagination={false} />
      )}
    </>
  );

  return (
    <Collapse
      style={{ marginBottom: 16, background: token.colorBgContainer }}
      items={[{
        key: section,
        label: <strong>{rows.length ? `${title} (${rows.length})` : title}</strong>,
        ...(extra ? { extra: <span onClick={(e) => e.stopPropagation()}>{extra}</span> } : {}),
        children: body,
      }]}
    />
  );
}
